import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import {
  AlertCircle,
  Check,
  CheckCircle2,
  FileCheck2,
  Hash,
  Loader2,
  Mail,
  Send,
  ShieldCheck,
  User,
} from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { LegalDocumentContent } from '../components/LegalDocumentContent';
import { useLegalDocument } from '../hooks/useLegalDocument';
import { functions } from '../firebase';

const initialForm = {
  decision: false,
  name: '',
  orderIdentifier: '',
  email: '',
  website: '',
};

const createSubmissionId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `withdrawal-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

const formatSubmittedAt = (value) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return String(value || '');
  return new Intl.DateTimeFormat('hu-HU', {
    dateStyle: 'long',
    timeStyle: 'medium',
    timeZone: 'Europe/Budapest',
  }).format(date);
};

export default function Withdrawal() {
  const { document } = useLegalDocument('withdrawal');
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [receipt, setReceipt] = useState(null);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setErrors((current) => ({ ...current, [name]: '' }));
    setSubmitError('');
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.decision) nextErrors.decision = 'Az elállási döntés megerősítése kötelező.';
    if (!form.name.trim()) nextErrors.name = 'Add meg a neved.';
    if (form.name.trim().length > 120) nextErrors.name = 'A név legfeljebb 120 karakter lehet.';
    if (form.orderIdentifier.trim().length < 3) {
      nextErrors.orderIdentifier = 'Add meg a rendelés vagy szerződés azonosítóját.';
    }
    if (form.orderIdentifier.trim().length > 80) {
      nextErrors.orderIdentifier = 'Az azonosító legfeljebb 80 karakter lehet.';
    }
    const email = form.email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      nextErrors.email = 'Adj meg egy érvényes e-mail-címet.';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate() || submitting) return;

    setSubmitting(true);
    setSubmitError('');
    const submissionId = createSubmissionId();
    try {
      const submitWithdrawal = httpsCallable(functions, 'submitWithdrawal');
      const response = await submitWithdrawal({
        decision: form.decision,
        name: form.name.trim(),
        orderIdentifier: form.orderIdentifier.trim(),
        email: form.email.trim(),
        website: form.website,
        submissionId,
      });
      const result = response.data || {};
      setReceipt({
        requestId: result.requestId || submissionId,
        submittedAt: result.submittedAt || new Date().toISOString(),
        submittedAtLabel: result.submittedAtLabel || '',
        confirmationSent: result.confirmationSent !== false,
        name: form.name.trim(),
        orderIdentifier: form.orderIdentifier.trim(),
        email: form.email.trim(),
      });
      setForm(initialForm);
    } catch (error) {
      if (error?.details?.received) {
        setReceipt({
          requestId: error.details.requestId || submissionId,
          submittedAt: new Date().toISOString(),
          confirmationSent: false,
          name: form.name.trim(),
          orderIdentifier: form.orderIdentifier.trim(),
          email: form.email.trim(),
        });
        return;
      }
      setSubmitError(
        error?.message
          ? `Az elállást nem sikerült elküldeni: ${error.message}`
          : 'Az elállást jelenleg nem sikerült elküldeni. Kérjük, próbáld újra.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="border-b border-primary/10 bg-secondary/55">
        <div className="container-zinga py-12 md:py-16">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#8A6A22]">
            Jogi dokumentumok
          </p>
          <h1 className="mt-3 max-w-3xl font-display text-3xl font-bold text-primary md:text-5xl">
            {document.title || 'Elállási jog'}
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-7 text-muted-foreground md:text-base">
            Itt online is közölheted velünk, hogy elállsz a megjelölt rendeléstől vagy szerződéstől.
            A funkció az elállási időszak alatt folyamatosan elérhető.
          </p>
        </div>
      </div>

      <div className="container-zinga py-12 md:py-16">
        {receipt ? (
          <section className="mb-10 overflow-hidden rounded-[2rem] border border-primary/15 bg-card shadow-soft" aria-live="polite">
            <div className="bg-primary px-6 py-8 text-primary-foreground sm:px-8">
              <CheckCircle2 className="h-10 w-10 text-[#D7B55B]" strokeWidth={1.8} />
              <h2 className="mt-4 font-display text-2xl font-semibold">
                Elállási nyilatkozatod beérkezett
              </h2>
              <p className="mt-2 text-sm leading-6 text-primary-foreground/75">
                Az alábbi adatokkal rögzítettük a nyilatkozatot.
              </p>
            </div>
            <div className="p-6 sm:p-8">
              <dl className="grid gap-5 sm:grid-cols-2">
                <div><dt className="text-xs text-muted-foreground">Elállási döntés</dt><dd className="mt-1 font-medium">Elállok a megjelölt szerződéstől.</dd></div>
                <div><dt className="text-xs text-muted-foreground">Név</dt><dd className="mt-1 font-medium">{receipt.name}</dd></div>
                <div><dt className="text-xs text-muted-foreground">Rendelés / szerződés</dt><dd className="mt-1 font-medium">{receipt.orderIdentifier}</dd></div>
                <div><dt className="text-xs text-muted-foreground">Visszaigazolási e-mail</dt><dd className="mt-1 font-medium">{receipt.email}</dd></div>
                <div><dt className="text-xs text-muted-foreground">Elküldés időpontja</dt><dd className="mt-1 font-medium">{receipt.submittedAtLabel || formatSubmittedAt(receipt.submittedAt)}</dd></div>
                <div><dt className="text-xs text-muted-foreground">Átvételi azonosító</dt><dd className="mt-1 break-all font-medium">{receipt.requestId}</dd></div>
              </dl>
              <div className={`mt-7 flex items-start gap-3 rounded-2xl border p-4 ${receipt.confirmationSent ? 'border-primary/15 bg-primary/[0.055]' : 'border-amber-300 bg-amber-50'}`}>
                {receipt.confirmationSent ? <Mail className="mt-0.5 h-5 w-5 shrink-0 text-primary" /> : <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />}
                <p className="text-sm leading-6">
                  {receipt.confirmationSent
                    ? 'Az átvételi elismervényt elküldtük a megadott e-mail-címre.'
                    : 'A nyilatkozatot rögzítettük, de az e-mailes elismervényt átmenetileg nem sikerült elküldeni. Kérjük, őrizd meg az átvételi azonosítót, és szükség esetén írj az info@lumenverde.hu címre.'}
                </p>
              </div>
              <button type="button" onClick={() => setReceipt(null)} className="btn-outline mt-7">
                Új elállási nyilatkozat
              </button>
            </div>
          </section>
        ) : (
          <div className="grid items-start gap-8 lg:grid-cols-[0.7fr_1.3fr]">
            <aside className="space-y-5 lg:sticky lg:top-28">
              <div className="rounded-3xl border border-primary/10 bg-secondary/50 p-6">
                <ShieldCheck className="h-7 w-7 text-primary" strokeWidth={1.8} />
                <h2 className="mt-4 font-display text-xl font-semibold text-primary">Mit kell megadnod?</h2>
                <ul className="mt-4 space-y-3 text-sm leading-6 text-foreground/75">
                  {['Az elállási döntésedet', 'A nevedet', 'A rendelés vagy szerződés azonosítóját', 'A visszaigazoláshoz használt e-mail-címet'].map((item) => (
                    <li key={item} className="flex gap-3"><Check className="mt-1 h-4 w-4 shrink-0 text-primary" strokeWidth={2.4} />{item}</li>
                  ))}
                </ul>
              </div>
              <div className="rounded-3xl border border-[#D9BE8B]/60 bg-[#FFF8E8] p-6">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8A6A22]">Más módon is elküldheted</p>
                <p className="mt-3 text-sm leading-6 text-foreground/75">
                  Az online funkció nem érinti azt a jogodat, hogy az elállást e-mailben,
                  postai úton vagy más egyértelmű nyilatkozattal közöld velünk.
                </p>
                <a href="mailto:info@lumenverde.hu" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
                  <Mail className="h-4 w-4" /> info@lumenverde.hu
                </a>
              </div>
            </aside>

            <section className="overflow-hidden rounded-[2rem] border border-primary/15 bg-card shadow-soft">
              <div className="border-b border-primary/10 bg-primary/[0.045] px-6 py-6 sm:px-8">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
                    <FileCheck2 className="h-5 w-5" strokeWidth={1.8} />
                  </span>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8A6A22]">Online elállási funkció</p>
                    <h2 className="mt-1 font-display text-2xl font-semibold text-primary">Elállás a szerződéstől</h2>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} noValidate className="space-y-6 p-6 sm:p-8">
                <div className="absolute -left-[10000px]" aria-hidden="true">
                  <label htmlFor="withdrawal-website">Weboldal</label>
                  <input id="withdrawal-website" name="website" value={form.website} onChange={handleChange} tabIndex={-1} autoComplete="off" />
                </div>

                <label className={`flex cursor-pointer items-start gap-3 rounded-2xl border p-4 transition-colors ${errors.decision ? 'border-destructive bg-destructive/5' : form.decision ? 'border-primary/30 bg-primary/[0.055]' : 'border-border bg-background'}`}>
                  <input type="checkbox" name="decision" checked={form.decision} onChange={handleChange} className="mt-1 h-4 w-4 accent-primary" />
                  <span>
                    <span className="block text-sm font-semibold text-foreground">Kijelentem, hogy elállok a megjelölt szerződéstől.</span>
                    <span className="mt-1 block text-xs leading-5 text-muted-foreground">Ezzel egyértelműen közlöd velünk az elállási döntésedet.</span>
                    {errors.decision ? <span className="mt-2 block text-xs text-destructive">{errors.decision}</span> : null}
                  </span>
                </label>

                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="withdrawal-name" className="mb-2 flex items-center gap-2 text-xs font-medium"><User className="h-4 w-4 text-primary" />Név</label>
                    <input id="withdrawal-name" name="name" value={form.name} onChange={handleChange} autoComplete="name" maxLength={120} className={`input-zinga ${errors.name ? 'border-destructive' : ''}`} placeholder="Teljes név" aria-invalid={Boolean(errors.name)} />
                    {errors.name ? <p className="mt-1.5 text-xs text-destructive">{errors.name}</p> : null}
                  </div>
                  <div>
                    <label htmlFor="withdrawal-order" className="mb-2 flex items-center gap-2 text-xs font-medium"><Hash className="h-4 w-4 text-primary" />Rendelés vagy szerződés azonosítója</label>
                    <input id="withdrawal-order" name="orderIdentifier" value={form.orderIdentifier} onChange={handleChange} maxLength={80} className={`input-zinga ${errors.orderIdentifier ? 'border-destructive' : ''}`} placeholder="pl. LV-123456" aria-invalid={Boolean(errors.orderIdentifier)} />
                    {errors.orderIdentifier ? <p className="mt-1.5 text-xs text-destructive">{errors.orderIdentifier}</p> : null}
                  </div>
                </div>

                <div>
                  <label htmlFor="withdrawal-email" className="mb-2 flex items-center gap-2 text-xs font-medium"><Mail className="h-4 w-4 text-primary" />E-mail-cím az átvételi elismervényhez</label>
                  <input id="withdrawal-email" type="email" name="email" value={form.email} onChange={handleChange} autoComplete="email" maxLength={160} className={`input-zinga ${errors.email ? 'border-destructive' : ''}`} placeholder="nev@pelda.hu" aria-invalid={Boolean(errors.email)} />
                  {errors.email ? <p className="mt-1.5 text-xs text-destructive">{errors.email}</p> : <p className="mt-1.5 text-xs text-muted-foreground">Erre a címre küldjük a nyilatkozat tartalmát és az elküldés időpontját tartalmazó elismervényt.</p>}
                </div>

                {submitError ? (
                  <div className="flex items-start gap-3 rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive" role="alert">
                    <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
                    <p>{submitError}</p>
                  </div>
                ) : null}

                <button type="submit" disabled={submitting} className="btn-primary min-h-12 w-full gap-2 disabled:cursor-not-allowed disabled:opacity-60">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {submitting ? 'Elállás küldése…' : 'Elállás megerősítése'}
                </button>
              </form>
            </section>
          </div>
        )}

        <section className="mt-12 border-t border-primary/10 pt-10">
          <h2 className="font-display text-2xl font-semibold text-primary">Részletes tájékoztató</h2>
          <div className="mt-5 rounded-3xl border border-border/70 bg-card p-6 sm:p-8">
            <LegalDocumentContent content={document.content} />
          </div>
        </section>
      </div>
    </Layout>
  );
}
